import axios from 'axios';
import { uploadService } from '@/apis/upload.s';
import imageHelpers from '@/helpers/image';

export default {
  data() {
    return {
      attachments: [],
      showAllAttachments: false,
    };
  },

  computed: {
    isUploadingAttachments() {
      return Array.isArray(this.attachments) && this.attachments.some(a => a && a.status === 'uploading');
    },
    visibleAttachments() {
      if (this.showAllAttachments) return this.attachments;
      return this.attachments.slice(0, this.maxPreviewThumbs);
    },
    hiddenCount() {
      if (this.showAllAttachments) return 0;
      return Math.max(0, this.attachments.length - this.maxPreviewThumbs);
    },
    maxPreviewThumbs() {
      return 3;
    },
    maxAttachments() {
      return 1;
    },
    maxFileSize() {
      return 5 * 1024 * 1024;
    },
    acceptedFileTypes() {
      return ['image/', 'video/'];
    },
  },

  methods: {
    notifyError(message) {
      this.$store.commit('setMessages', {
        type: 'error',
        messages: message,
      });
    },

    isAcceptedFileType(file) {
      const type = typeof file?.type === 'string' ? file.type : '';
      return this.acceptedFileTypes.some(prefix => type.startsWith(prefix));
    },

    getFileExtension(file) {
      const name = String(file?.name || '').trim();
      if (name.includes('.')) return name.split('.').pop();
      if (file?.type?.startsWith('image/')) return 'png';
      if (file?.type?.startsWith('video/')) return 'mp4';
      return 'bin';
    },

    onFilesSelected(e) {
      const list = e.target.files ? Array.from(e.target.files) : [];
      const accepted = list.filter(f => this.isAcceptedFileType(f));
      const remainingSlots = Math.max(0, this.maxAttachments - this.attachments.length);

      if (remainingSlots <= 0 && accepted.length > 0) {
        this.notifyError(
          `You can only upload up to ${this.maxAttachments} ${
            this.maxAttachments > 1 ? 'images' : 'image'
          } per message.`,
        );
      }

      accepted.slice(0, remainingSlots).forEach(file => this.addAttachment(file));
      if (accepted.length > 0) this.showAllAttachments = false;
      e.target.value = '';
    },

    addAttachment(file) {
      const id = this.objectId();
      const localUrl = URL.createObjectURL(file);
      const isVideo = typeof file?.type === 'string' && file.type.startsWith('video/');
      const item = {
        id,
        file,
        name: file.name,
        url: localUrl,
        localUrl,
        remoteUrl: '',
        status: 'uploading',
        isVideo,
      };
      this.attachments.push(item);
      this.uploadAttachment(item);
    },

    async uploadAttachment(item) {
      try {
        const file = item?.file;
        if (!file) throw new Error('Missing file');
        if (file.size > this.maxFileSize) {
          const mb = Math.round(this.maxFileSize / 1024 / 1024);
          throw new Error(`File must be less than ${mb}MB`);
        }

        const ext = this.getFileExtension(file);
        const filename = `attachment-${item.id}.${ext}`;

        const resGetImage = await uploadService.get({ filename });
        const presignedUrl = resGetImage?.data?.presignedUrl;
        const imagePath = resGetImage?.data?.imagePath;
        if (!presignedUrl || !imagePath) throw new Error('Invalid upload response');

        await axios.put(presignedUrl, file, {
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
        });

        const remoteUrl = imageHelpers.url(imagePath);
        item.remoteUrl = remoteUrl;
        item.url = remoteUrl;
        item.status = 'ready';

        if (item.localUrl) URL.revokeObjectURL(item.localUrl);
        item.localUrl = '';
      } catch (err) {
        item.status = 'error';
      }
    },

    objectId() {
      return this.hex(Date.now() / 1000) + ' '.repeat(16).replace(/./g, () => this.hex(Math.random() * 16));
    },

    hex(value) {
      return Math.floor(value).toString(16);
    },

    removeAttachment(id) {
      const idx = this.attachments.findIndex(a => a.id === id);
      if (idx === -1) return;
      const [removed] = this.attachments.splice(idx, 1);
      if (removed?.localUrl) URL.revokeObjectURL(removed.localUrl);
      if (removed?.url && String(removed.url).startsWith('blob:')) URL.revokeObjectURL(removed.url);
      if (this.attachments.length <= this.maxPreviewThumbs) this.showAllAttachments = false;
    },
  },
};
